<template>
    <div class="mcp-documentation-reference">
        <h4 class="section-title">{{ $t('dash.settings.mcp_docs.reference.title') }}</h4>

        <div class="intro-panel">
            <p>{{ $t('dash.settings.mcp_docs.reference.intro.p1') }}</p>
            <p>{{ $t('dash.settings.mcp_docs.reference.intro.p2') }}</p>
            <ul>
                <li v-html="$t('dash.settings.mcp_docs.reference.intro.endpoint')"></li>
                <li v-html="$t('dash.settings.mcp_docs.reference.intro.auth')"></li>
                <li v-html="$t('dash.settings.mcp_docs.reference.intro.envelope')"></li>
            </ul>
        </div>

        <div v-for="category in categories" :key="category.key" class="category-block">
            <div class="category-header" @click="toggleCategory(category.key)">
                <i :class="category.icon"></i>
                <span class="category-name">{{ $t('dash.settings.mcp_docs.categories.' + category.key) }}</span>
                <span class="category-count">{{ category.tools.length }}</span>
                <i class="chevron" :class="isOpen(category.key) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </div>

            <div v-if="isOpen(category.key)" class="category-tools">
                <div v-for="toolName in category.tools" :key="toolName" class="tool-row">
                    <div class="tool-left">
                        <div class="tool-heading">
                            <h5>{{ toolName }}</h5>
                            <span :class="['badge', tools[toolName].readOnly ? 'badge-read' : 'badge-write']">
                                {{ $t(tools[toolName].readOnly ? 'dash.settings.mcp_docs.reference.read_only' : 'dash.settings.mcp_docs.reference.write') }}
                            </span>
                        </div>
                        <p class="tool-description">{{ $t('dash.settings.mcp_docs.tools.' + toolName + '.description') }}</p>

                        <table v-if="tools[toolName].params.length" class="params-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('dash.settings.mcp_docs.reference.table.param') }}</th>
                                    <th>{{ $t('dash.settings.mcp_docs.reference.table.type') }}</th>
                                    <th>{{ $t('dash.settings.mcp_docs.reference.table.required') }}</th>
                                    <th>{{ $t('dash.settings.mcp_docs.reference.table.description') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="param in tools[toolName].params" :key="param.name">
                                    <td><code>{{ param.name }}</code></td>
                                    <td class="type-cell">{{ param.type }}</td>
                                    <td>
                                        <span :class="['req-badge', param.required ? 'req-yes' : 'req-no']">
                                            {{ $t(param.required ? 'dash.settings.mcp_docs.reference.required' : 'dash.settings.mcp_docs.reference.optional') }}
                                        </span>
                                    </td>
                                    <td>{{ $t('dash.settings.mcp_docs.tools.' + toolName + '.params.' + param.name + '.description') }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-else class="no-params">{{ $t('dash.settings.mcp_docs.reference.no_params') }}</p>

                        <div class="response-format">
                            <span class="response-label">{{ $t('dash.settings.mcp_docs.reference.response_label') }}</span>
                            <p>{{ $t('dash.settings.mcp_docs.tools.' + toolName + '.response') }}</p>
                        </div>
                    </div>

                    <div class="tool-right">
                        <mcp-code-example :tool-name="toolName" :example-args="tools[toolName].exampleArgs"></mcp-code-example>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import mcpCodeExample from './mcp-code-example.vue'
import { TOOL_CATEGORIES, TOOLS } from './mcp-documentation-data'

export default {
    name: 'mcp-documentation-reference',

    components: {
        'mcp-code-example': mcpCodeExample
    },

    data() {
        return {
            categories: TOOL_CATEGORIES,
            tools: TOOLS,
            // Every category open by default - it's a reference page, people scan/search
            // rather than click through an empty accordion first.
            openCategories: TOOL_CATEGORIES.map(c => c.key)
        }
    },

    methods: {
        isOpen(categoryKey) {
            return this.openCategories.includes(categoryKey)
        },

        toggleCategory(categoryKey) {
            this.openCategories = this.isOpen(categoryKey)
                ? this.openCategories.filter(k => k !== categoryKey)
                : [...this.openCategories, categoryKey]
        }
    }
}
</script>

<style lang="scss" scoped>
.mcp-documentation-reference {
    .section-title {
        font-weight: 600;
        font-size: 15px;
        margin: 0 0 14px;
    }

    .intro-panel {
        background: #fff;
        border: 1px solid #e3e6ee;
        border-radius: 10px;
        padding: 18px 20px;
        margin-bottom: 24px;
        font-size: 13px;
        color: #333;
        line-height: 1.6;

        p { margin: 0 0 10px; }

        ul {
            margin: 10px 0 0;
            padding-left: 18px;

            li { margin-bottom: 6px; }
        }

        ::v-deep code {
            background: #F1EDF5;
            padding: 1px 5px;
            border-radius: 4px;
            font-size: 12px;
            color: #6B1F4D;
        }
    }

    .category-block {
        margin-bottom: 12px;
        border: 1px solid #e3e6ee;
        border-radius: 10px;
        overflow: hidden;

        .category-header {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 14px 18px;
            background: #faf8fc;
            cursor: pointer;

            i:first-child { color: #6B1F4D; font-size: 14px; }

            .category-name {
                font-weight: 600;
                font-size: 13.5px;
                color: #333;
            }

            .category-count {
                background: #F1EDF5;
                color: #6B1F4D;
                font-size: 11px;
                font-weight: 700;
                border-radius: 10px;
                padding: 1px 8px;
            }

            .chevron {
                margin-left: auto;
                color: #9ba3b7;
                font-size: 12px;
            }
        }

        .category-tools {
            padding: 4px 18px 18px;
        }
    }

    .tool-row {
        display: flex;
        gap: 24px;
        padding: 20px 0;
        border-top: 1px solid #f0eef4;

        &:first-child { border-top: none; }

        .tool-left {
            flex: 2;
            min-width: 0;
        }

        .tool-right {
            flex: 1;
            min-width: 0;
            position: sticky;
            top: 20px;
            align-self: flex-start;
        }
    }

    .tool-heading {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;

        h5 {
            font-family: 'SFMono-Regular', Consolas, monospace;
            font-size: 14px;
            font-weight: 700;
            color: #6B1F4D;
            margin: 0;
        }

        .badge {
            font-size: 10.5px;
            font-weight: 700;
            border-radius: 8px;
            padding: 2px 8px;
            text-transform: uppercase;
        }

        .badge-read { background: #e7f3ff; color: #1a73c7; }
        .badge-write { background: #fdeee8; color: #c85a2c; }
    }

    .tool-description {
        font-size: 13px;
        color: #555e75;
        line-height: 1.55;
        margin: 0 0 14px;
    }

    .params-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 14px;
        font-size: 12.5px;

        th {
            text-align: left;
            color: #9ba3b7;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            padding: 6px 8px;
            border-bottom: 1px solid #e3e6ee;
        }

        td {
            padding: 8px 8px;
            border-bottom: 1px solid #f0eef4;
            vertical-align: top;
            color: #333;
        }

        code {
            background: #F1EDF5;
            padding: 1px 5px;
            border-radius: 4px;
            font-size: 12px;
            color: #6B1F4D;
        }

        .type-cell {
            font-family: 'SFMono-Regular', Consolas, monospace;
            font-size: 11.5px;
            color: #555e75;
            white-space: nowrap;
        }

        .req-badge {
            font-size: 10.5px;
            font-weight: 600;
            border-radius: 6px;
            padding: 2px 7px;
            white-space: nowrap;
        }

        .req-yes { background: #fdeee8; color: #c85a2c; }
        .req-no { background: #eef1f6; color: #7a8299; }
    }

    .no-params {
        font-size: 12.5px;
        color: #9ba3b7;
        margin: 0 0 14px;
        font-style: italic;
    }

    .response-format {
        .response-label {
            display: block;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            color: #9ba3b7;
            margin-bottom: 4px;
        }

        p {
            font-size: 12.5px;
            color: #555e75;
            margin: 0;
            line-height: 1.5;
        }
    }
}

@media (max-width: 900px) {
    .mcp-documentation-reference .tool-row {
        flex-direction: column;

        .tool-right {
            position: static;
        }
    }
}
</style>
